import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchSetupStatus } from "../services/onboardingService";

let cachedStatus = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5000;
const listeners = new Set();
const SETUP_GUIDE_UPDATED = "setup-guide-status-updated";
const SETUP_GUIDE_INVALIDATE = "setup-guide-status-invalidate";

function publishStatus(data) {
  cachedStatus = data;
  cacheTimestamp = data ? Date.now() : 0;
  listeners.forEach((listener) => {
    try {
      listener(data);
    } catch {
      /* ignore subscriber errors */
    }
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SETUP_GUIDE_UPDATED, { detail: data }));
  }
}

/**
 * Clear shared cache and ask every mounted hook to refetch.
 * Call after actions that change onboarding progress (store/branch/apps/etc.).
 */
export function invalidateSetupGuideCache() {
  cachedStatus = null;
  cacheTimestamp = 0;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SETUP_GUIDE_INVALIDATE));
  }
}

/**
 * Shared setup guide status for SetupGuidePage and TopNavbar badge.
 * All hook instances stay in sync via an in-memory pub/sub + route refetch.
 */
export function useSetupGuideStatus({ enabled = true } = {}) {
  const location = useLocation();
  const [data, setData] = useState(() => (enabled ? cachedStatus : null));
  const [loading, setLoading] = useState(Boolean(enabled && !cachedStatus));
  const [error, setError] = useState(null);

  const refetch = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled) return null;

      const now = Date.now();
      if (!force && cachedStatus && now - cacheTimestamp < CACHE_TTL_MS) {
        setData(cachedStatus);
        setLoading(false);
        return cachedStatus;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchSetupStatus();
        publishStatus(response);
        setData(response);
        return response;
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [enabled]
  );

  // Keep every hook instance in sync when another instance refreshes the cache.
  useEffect(() => {
    if (!enabled) return undefined;

    const onPublish = (next) => {
      setData(next);
      if (next) setLoading(false);
    };
    listeners.add(onPublish);
    return () => listeners.delete(onPublish);
  }, [enabled]);

  // Refetch when route changes (e.g. user finishes add-store and returns to dashboard).
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    refetch({ force: true });
    return undefined;
  }, [enabled, location.pathname, refetch]);

  // Refetch on tab focus / visibility and when cache is explicitly invalidated.
  useEffect(() => {
    if (!enabled) return undefined;

    const forceRefetch = () => {
      refetch({ force: true });
    };
    const handleFocus = () => forceRefetch();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") forceRefetch();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(SETUP_GUIDE_INVALIDATE, forceRefetch);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(SETUP_GUIDE_INVALIDATE, forceRefetch);
    };
  }, [enabled, refetch]);

  const progress = data?.progress ?? null;
  const steps = data?.steps ?? null;

  const remainingCoreSteps = useMemo(() => {
    if (!progress) return null;
    if (typeof progress.remainingCoreSteps === "number") {
      return progress.remainingCoreSteps;
    }
    return Math.max(
      0,
      (progress.totalCoreSteps || 0) - (progress.completedCoreSteps || 0)
    );
  }, [progress]);

  const headerStepNumber = useMemo(() => {
    if (!progress) return null;
    if (progress.completedCoreSteps >= progress.totalCoreSteps) return null;
    return Math.min(progress.completedCoreSteps + 1, progress.totalCoreSteps);
  }, [progress]);

  const allCoreStepsComplete = useMemo(() => {
    if (!progress) return false;
    return progress.completedCoreSteps >= progress.totalCoreSteps;
  }, [progress]);

  return {
    data,
    steps,
    progress,
    loading,
    error,
    refetch,
    headerStepNumber,
    remainingCoreSteps,
    allCoreStepsComplete,
  };
}
